import React, { useState } from "react";
import { useWebHaptics } from "web-haptics/react";
import { useVariantCopy } from "./useVariantCopy";
import { mainUrl } from "../../utils/variant";
import { PACKAGES, DEFAULT_PACKAGE, packageFor, money } from "../../config/packages";

// Variant landing-page pricing (startviralgpt.com).
//
// Deliberately minimal: the decision here is "how many followers", so the
// follower count is the largest thing on each card and everything that isn't
// quantity or price is gone — no plan names, no blurbs, no feature lists with
// tooltips. The previous version cloned the
// full subscription card and read as three dense columns of claims.
//
// Prices come from config/packages.js, the same source the main pricing page,
// the visualizer and the /50off funnel read. The CTA still routes to /50off on
// the main site with attribution forwarded, so the checkout and the
// post-purchase upsells are untouched.
const Pricing = () => {
  const L = useVariantCopy();
  const T = L.packages;
  const { trigger: haptic } = useWebHaptics();
  const [selected, setSelected] = useState(DEFAULT_PACKAGE);
  const active = packageFor(selected);

  return (
    <section id="pricing" className="stripe-section--gradient pt-5 pb-16">
      <div className="container mx-auto text-center stripe-container">

        <div className="stripe-pricing-header">
          <h2 className="mt-2 stripe-heading stripe-heading--xl">{T.heading}</h2>
          <p className="mt-3 mx-auto stripe-body stripe-body--lg stripe-body-narrow">
            {T.subheading}
          </p>
        </div>

		<div
		  className="mx-auto mt-3 d-inline-flex align-items-center justify-content-center"
		  style={{ color: '#166534', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 999, padding: '7px 10px', fontSize: 'clamp(0.62rem, 3vw, 0.78rem)', fontWeight: 700, whiteSpace: 'nowrap', maxWidth: '100%' }}
		>
		  {T.valueProp}
		</div>

        <div
          className="mx-auto mt-4 px-2"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
            gap: "14px",
            maxWidth: 720,
          }}
        >
          {PACKAGES.map((pkg) => {
            const isSelected = pkg.key === selected;
			const isPopular = !!pkg.popular;
            return (
              <button
                key={pkg.key}
                type="button"
                onClick={() => { haptic(15); setSelected(pkg.key); }}
                aria-pressed={isSelected}
                style={{
                  position: "relative",
                  textAlign: "center",
                  background: isSelected ? "linear-gradient(180deg, #f5f3ff 0%, #fff 100%)" : "#fff",
                  border: isSelected ? "3px solid #635bff" : isPopular ? "1.5px solid #b9afff" : "1px solid #e3e8ee",
                  borderRadius: 16,
                  padding: isPopular ? "1.75rem 1rem 1.45rem" : "1.6rem 1rem 1.35rem",
                  cursor: "pointer",
                  boxShadow: isSelected ? "0 14px 32px rgba(99,91,255,0.24)" : isPopular ? "0 5px 14px rgba(99,91,255,0.10)" : "0 2px 4px rgba(50,50,93,0.06)",
                  transform: isSelected ? "translateY(-4px) scale(1.02)" : "none",
                  zIndex: isSelected ? 1 : 0,
                  transition: "all 0.15s ease",
                }}
              >
                {(pkg.popular || pkg.bestValue) && (
                  <span
                    style={{
                      position: "absolute", top: -11, left: "50%", transform: "translateX(-50%)",
                      background: pkg.popular
                        ? "linear-gradient(135deg, #635bff 0%, #7c3aed 100%)"
                        : "#0a2540",
                      color: "#fff", fontSize: pkg.popular ? "0.72rem" : "0.66rem", fontWeight: 800,
                      padding: pkg.popular ? "5px 15px" : "4px 11px", borderRadius: 999, whiteSpace: "nowrap",
					  boxShadow: pkg.popular ? "0 4px 12px rgba(99,91,255,0.32)" : "none",
                    }}
                  >
                    {pkg.popular ? T.mostPopular : T.bestValue}
                  </span>
                )}

                {/* Quantity leads — it is the only thing being chosen here. */}
                <div style={{ fontSize: "1.75rem", fontWeight: 800, color: "#0a2540", letterSpacing: "-0.5px", lineHeight: 1.1 }}>
                  {pkg.followers.toLocaleString()}
                </div>
                <div style={{ color: "#68778d", fontSize: "0.82rem", marginTop: 2 }}>
                  {T.followers}
                </div>

				<div className="d-flex align-items-baseline justify-content-center" style={{ gap: 7, marginTop: 12 }}>
				  <span style={{ color: '#94a3b8', fontSize: '0.88rem', textDecoration: 'line-through', fontWeight: 600 }}>
					${money(pkg.anchor)}
				  </span>
				  {/* Selection only — same fix as the Visualizer's picker. Colouring
				      the popular price blue as well left $9.99 looking chosen no
				      matter which pack was actually selected. */}
				  <span style={{ fontSize: isPopular ? "1.7rem" : "1.5rem", fontWeight: 800, color: isSelected ? "#635bff" : "#0a2540", letterSpacing: "-0.5px" }}>
					${money(pkg.price)}
				  </span>
                </div>
                <div style={{ color: "#8898aa", fontSize: "0.72rem" }}>{T.oneTime}</div>
              </button>
            );
          })}
        </div>

        {/* One CTA, naming the selection, so it always matches the chosen card. */}
        <div className="mt-4">
          <a
            href={mainUrl(`/50off?plan=${active.key}&from=pricing`)}
            onClick={() => haptic(15)}
            className="stripe-btn stripe-btn--primary"
            style={{ display: "inline-block", padding: "0.95rem 2.25rem", fontSize: "1.05rem", fontWeight: 700 }}
          >
            {T.cta(active.followers.toLocaleString(), money(active.price))}
          </a>
          <p className="stripe-body stripe-body--sm stripe-text-muted" style={{ marginTop: 12, marginBottom: 0 }}>
            {T.noSubscription}
          </p>
        </div>

      </div>
    </section>
  );
};

export default Pricing;
